import { getAssetChannelLabel, getAssetLabel } from 'src/data/asset';
import {
  convertRewriteToolToSuggestTool,
  doesToolSupportsSubTemplates,
  GENERATION_TOOL,
  type GenerationTool,
  getToolContentType,
  getToolDescription,
  getToolTitle,
  isBasicContentImproverTool,
  isFreestyleGenerationTool,
  isImageGenerationTool,
  isLongFormLocalizationTool,
  isMixtureTool,
  isMultiPromptTool,
  isRewriteGenerationTool,
} from 'src/data/generationTool';
import { type MultiPromptTemplate } from 'src/data/template';
import { AI_CHAT_LABEL, CONTENT_IMPROVER_LABEL } from 'src/data/ui';
import type { ID } from 'src/lib/collection';

import {
  AI_GENERATION_MODE,
  AI_RECOMPOSE_MODE,
  GENERATION_SOURCE_TYPE,
  type GenerationSourceType,
} from './const';
import type { GenerationRequest, RecomposeRequest, SuggestionRequest } from './types';

export const getGenerationResultDescription = (
  request: GenerationRequest,
  multiTemplate?: MultiPromptTemplate
) => {
  const { generationTool, chainGenerationTool, assetType, sourceType } = request;

  const alignedTool = convertRewriteToolToSuggestTool(generationTool);
  let title = '';
  let description = '';

  if (sourceType === GENERATION_SOURCE_TYPE.aiChat) {
    title = AI_CHAT_LABEL;
  } else if (
    isSectionRecomposeResult(request) || // new "improve sections" results
    isBasicContentImproverTool(alignedTool) || // old "recompose" results with "basic content improver" tool
    isRewriteGenerationTool(generationTool)
  ) {
    title = CONTENT_IMPROVER_LABEL;
  } else if (chainGenerationTool) {
    if (doesToolSupportsSubTemplates(chainGenerationTool)) {
      title = formatToolWithSubTemplatesTitle(request, multiTemplate);
    } else if (isFreestyleGenerationTool(chainGenerationTool)) {
      title = getToolContentType(chainGenerationTool) || '';
    } else {
      title = prependTemplate(getToolTitle(chainGenerationTool));
    }
  } else if (isLongFormLocalizationTool(alignedTool)) {
    title = 'General prompt';

    // other tools
  } else if (alignedTool) {
    if (doesToolSupportsSubTemplates(request?.generationTool)) {
      title = formatToolWithSubTemplatesTitle(request, multiTemplate);
    } else if (isFreestyleGenerationTool(alignedTool)) {
      title = getToolContentType(alignedTool) || '';
    } else {
      title = prependTemplate(getToolContentType(alignedTool));
    }

    description = getToolDescription(alignedTool) || '';
    if (
      !isMixtureTool(alignedTool) &&
      !isMultiPromptTool(alignedTool) &&
      !isImageGenerationTool(alignedTool) &&
      !isFreestyleGenerationTool(alignedTool) &&
      !isRewriteGenerationTool(generationTool)
    ) {
      title += ` - ${getAssetLabel(assetType)}`;

      if (doesToolSupportsSubTemplates(request?.generationTool)) {
        title = formatToolWithSubTemplatesTitle(request, multiTemplate);
      }
    }
  } else {
    if (doesToolSupportsSubTemplates(request?.generationTool)) {
      title = formatToolWithSubTemplatesTitle(request, multiTemplate);
    } else {
      title = prependTemplate(getAssetChannelLabel(assetType) || '');
    }
    description = getAssetLabel(assetType) || '';
  }

  if (!description && isSuggestGenerationResult(request) && request.groupId) {
    description = getAssetLabel(assetType) || '';
  }

  const source = describeSource(sourceType);

  return {
    title,
    description,
    source,
  };
};

const describeSource = (sourceType: GenerationSourceType): string => {
  switch (sourceType) {
    case GENERATION_SOURCE_TYPE.improveListScore:
      return CONTENT_IMPROVER_LABEL;
    case GENERATION_SOURCE_TYPE.changeTov:
      return 'More like this - change tone of voice';
    default:
      return '';
  }
};

const formatToolWithSubTemplatesTitle = (
  request: GenerationRequest,
  multiTemplate?: MultiPromptTemplate
): string => {
  const baseName = multiTemplate?.name || getToolTitle(request?.generationTool);
  return prependTemplate(baseName);
};

const prependTemplate = (str?: string): string => {
  if (!str) {
    return '';
  }

  return `Template – ${str}`;
};

export const isSuggestGenerationResult = (
  result: GenerationRequest
): result is SuggestionRequest => (result ? Object.hasOwn(result, 'suggestionMode') : false);

export const isRecomposeGenerationResult = (
  result: GenerationRequest
): result is RecomposeRequest => (result ? Object.hasOwn(result, 'recomposeMode') : false);

export const getGenerationResultListId = (result: GenerationRequest): string =>
  isSuggestGenerationResult(result)
    ? `${AI_GENERATION_MODE.suggest}_${result?.id}`
    : `${AI_GENERATION_MODE.recompose}_${result?.id}`;

export const getGenerationRequestTool = (request: GenerationRequest): GenerationTool => {
  if (isSectionRecomposeResult(request)) {
    return GENERATION_TOOL.basicContentImprover;
  }
  if (isSuggestGenerationResult(request)) {
    return request.chainGenerationTool || request.generationTool;
  }
  return request.generationTool;
};

export const isPartialGenerationResultId = (id: ID): boolean => parseInt(String(id)) < 0;

export const isSectionRecomposeResult = (result: GenerationRequest) => {
  return isRecomposeGenerationResult(result) && result.chainId != null;
};

export const isFailedRecomposeGenerationResult = (result: GenerationRequest): boolean => {
  // consider only present "recompose" results
  if (!result) {
    return false;
  }
  if (!isRecomposeGenerationResult(result)) {
    return false;
  }

  const { variations } = result;

  // if there are no variations, consider it failed
  if (variations.length === 0) {
    return true;
  }

  // if there is only one variation, check its properties
  if (variations.length === 1) {
    const firstVariation = variations[0];

    // if the recompose mode is "keep as is", consider it "not failed", because user wanted to keep the original content
    if (firstVariation.recomposeMode === AI_RECOMPOSE_MODE.keepAsIs) {
      return false;
    }

    // if this is an "original" variation and has no sub-variations, consider it failed
    return !firstVariation?.originalVariationId && !firstVariation.variations?.length;
  }

  return false;
};
